import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, ViewChild } from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { FilterableItemsListComponent, FilterableListItemDirective } from '@examdojo/core/filterable-items-list';
import { IconComponent } from '@examdojo/core/icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, map, startWith, switchMap } from 'rxjs';
import { TopicStatus, TopicStatusToUI } from '../model';
import { TopicStatusIconComponent } from './topic-status-icon.component';

@UntilDestroy()
@Component({
  selector: 'dojo-topic-status',
  templateUrl: './topic-status.component.html',
  styleUrls: ['./topic-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FontAwesomeModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    FilterableItemsListComponent,
    MatMenuModule,
    IconComponent,
    NgClass,
    TopicStatusIconComponent,
    FilterableListItemDirective,
    ButtonComponent,
  ],
})
export class TopicStatusComponent {
  readonly status = input<TopicStatus>();
  readonly formCtrl = input<FormControl<TopicStatus>>(new FormControl<TopicStatus>('draft', { nonNullable: true }));
  readonly editable = input(false);

  private readonly status$ = combineLatest([
    toObservable(this.formCtrl).pipe(switchMap((fc) => fc.valueChanges.pipe(startWith(fc.value)))),
    toObservable(this.status),
  ]).pipe(map(([formControlValue, value]) => value ?? formControlValue ?? null));

  readonly statusChanged = outputFromObservable<TopicStatus>(this.status$);

  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;

  readonly allStatuses = Object.values(TopicStatusToUI);

  readonly state = connectState({
    status: this.status$.pipe(map((status) => TopicStatusToUI[status])),
  });

  readonly filterPropertyValueFn = (item: (typeof this.allStatuses)[number]) => item.label;
  readonly identityFn = (item: (typeof this.allStatuses)[number]) => item.id;

  setSelected(value: string) {
    this.formCtrl().setValue(value as TopicStatus);
    this.menuTrigger?.closeMenu();
  }
}
